/* tslint:disable:max-line-length */

import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import { APIConfigService } from '../apiconfig.service';

import * as __utils from '../yasag-utils';

import * as __model from '../model';

export interface ListParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface CreateParams {
  data: __model.Especial;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ExportSpecialParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ImportSpecialParams {
  data: __model.ImportCSV;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ReadParams {
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this especial. */
  codi_especial: string;
}

export interface UpdateParams {
  data: __model.Especial;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this especial. */
  codi_especial: string;
}

export interface PartialUpdateParams {
  data: __model.Especial;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this especial. */
  codi_especial: string;
}

export interface DeleteParams {
  /** A unique value identifying this especial. */
  codi_especial: string;
}

export interface DeleteImageParams {
  /** A unique value identifying this especial. */
  codi_especial: string;
}

export interface UploadImageParams {
  /** Image file to upload */
  image: File;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this especial. */
  codi_especial: string;
}

@Injectable()
export class EspecialService {
  constructor(
    private http: HttpClient,
    private apiConfigService: APIConfigService) {}


  /** Gets a list of attribute categories */
  list(params: ListParams, multipart = false): Observable<__model.EspecialList> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get<__model.EspecialList>(this.apiConfigService.options.apiUrl + `/especial/`, {params: queryParams});
  }

  /** Creates an attribute category */
  create(params: CreateParams, multipart = false): Observable<__model.Especial> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post<__model.Especial>(this.apiConfigService.options.apiUrl + `/especial/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Endpoint to export especial to CSV */
  exportSpecial(params: ExportSpecialParams, multipart = false): Observable<string> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get(this.apiConfigService.options.apiUrl + `/especial/export_special/`, {params: queryParams, responseType: 'text'});
  }

  /** Endpoint to import especial from CSV */
  importSpecial(params: ImportSpecialParams, multipart = false): Observable<string> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post(this.apiConfigService.options.apiUrl + `/especial/import_special/`, bodyParamsWithoutUndefined, {params: queryParams, responseType: 'text'});
  }

  /** Gets an attribute category with its attributes */
  read(params: ReadParams, multipart = false): Observable<__model.Especial> {
    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_especial: params.codi_especial,
    };
    return this.http.get<__model.Especial>(this.apiConfigService.options.apiUrl + `/especial/${pathParams.codi_especial}/`, {params: queryParams});
  }

  /** Updates an attribute category */
  update(params: UpdateParams, multipart = false): Observable<__model.Especial> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_especial: params.codi_especial,
    };
    return this.http.put<__model.Especial>(this.apiConfigService.options.apiUrl + `/especial/${pathParams.codi_especial}/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Updates an attribute category partially */
  partialUpdate(params: PartialUpdateParams, multipart = false): Observable<__model.Especial> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_especial: params.codi_especial,
    };
    return this.http.patch<__model.Especial>(this.apiConfigService.options.apiUrl + `/especial/${pathParams.codi_especial}/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Deletes an attribute category from the DB */
  delete(params: DeleteParams, multipart = false): Observable<string> {
    const pathParams = {
      codi_especial: params.codi_especial,
    };
    return this.http.delete(this.apiConfigService.options.apiUrl + `/especial/${pathParams.codi_especial}/`, {responseType: 'text'});
  }

  /** Delete especial icon image */
  deleteImage(params: DeleteImageParams, multipart = false): Observable<__model.Especial> {
    const pathParams = {
      codi_especial: params.codi_especial,
    };
    return this.http.delete<__model.Especial>(this.apiConfigService.options.apiUrl + `/especial/${pathParams.codi_especial}/delete_image/`);
  }

  /** Upload image for especial icon */
  uploadImage(params: UploadImageParams, multipart = false): Observable<__model.Especial> {
    const formDataParams = {
      image: params.image,
    };
    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_especial: params.codi_especial,
    };
    return this.http.post<__model.Especial>(this.apiConfigService.options.apiUrl + `/especial/${pathParams.codi_especial}/upload_image/`, formDataParams, {params: queryParams});
  }
}
